import { Dispatch, FC, useCallback, useEffect, useState } from "react";
import { Controller, FieldErrorsImpl, useForm } from "react-hook-form";
import Button from "components/atoms/button/Button";
import IconButton from "components/atoms/button/IconButton";
import EmptyEntity from "components/atoms/empty-entity/EmptyEntity";
import Form from "components/atoms/form/Form";
import Icon from "components/atoms/icon/Icon";
import Input from "components/atoms/input/Input";
import LoadingScreen from "components/atoms/loading-screen/LoadingScreen";
import NoDataBoundary from "components/atoms/no-data-boundary/NoDataBoundary";
import Accordion from "components/molecules/accordion/Accordion";
import AccordionBody from "components/molecules/accordion/AccordionBody/AccordionBody";
import AccordionTitle from "components/molecules/accordion/AccordionTitle/AccordionTitle";
import PrimaryDatePicker from "components/molecules/datepicker/PrimaryDatePicker";
import InputsFolder from "components/molecules/inputs-folder/InputsFolder";
import { IconType } from "lib/helpers/constants/iconTypes";
import { usePartners } from "lib/hooks/queries/Partners/usePartnersForFilter";
import { useUpdateCustomerProduct } from "lib/hooks/mutations/Customers/useUpdateCustomerProduct";
import ConditionalRender from "components/atoms/conditional-render/ConditionalRender";
import Label from "components/atoms/input/input-label/InputLabel";
import { customStyles } from "lib/helpers/configs/customStyles";
import Creatable from "react-select/creatable";
import { useTranslation } from "react-i18next";
import { usePermissions } from "lib/hooks/shared/usePermissions";
import CurrencyInput from "components/atoms/input/currency-input/CurrencyInput";
import DeleteProductModal from "../delete-product-modal/DeleteProductModal";

export interface IChooseProductsProps<T> {
  data?: T;
  setFormStep?: (value: number) => void;
  setFormDataOfProducts?: Dispatch<any>;
  isLoading: boolean;
  id: any;
  from_user_id: any;
  refetchProducts: () => void;
  themeColors: {
    primary: string;
  };
}

const UpdateChooseProducts: FC<IChooseProductsProps<any>> = ({
  setFormStep,
  data,
  setFormDataOfProducts,
  isLoading,
  id,
  from_user_id,
  refetchProducts,
  themeColors,
}) => {
  const {
    formState: { errors },
    handleSubmit,
    control,
    register,
    reset,
    watch,
  } = useForm({
    defaultValues: {
      products: data?.data?.map?.((product: any) => ({
        id: product?.id,
        creation_date: product?.creation_date,
        company_name: product?.company_name,
        product_name: product?.product_name,
        name: product?.name,
        status: product?.status,
        last_update: product?.updated_at,
        commission: product?.commission || 0,
        commission_date: product?.commission_date,
        commission_expected: product?.commission_expected || "",
        commission_expected_difference: product?.commission_expected_difference,
        premium_per_month: product?.premium_per_month?.toFixed(2) || "",
        contract_end_date: product?.contract_end_date || null,
        contract_start_date: product?.contract_start_date || null,
      })),
    },
  });



  useEffect(() => {
    reset({
      products: data?.data?.map?.((product: any) => ({
        id: product?.id,
        creation_date: product?.creation_date,
        company_name: product?.company_name,
        product_name: product?.product_name,
        name: product?.name,
        status: product?.status,
        last_update: product?.updated_at,
        commission: product?.commission || "",
        commission_date: product?.commission_date,
        commission_expected: product?.commission_expected,
        premium_per_month: product?.premium_per_month || "",
        contract_end_date: product?.contract_end_date || null,
        contract_start_date: product?.contract_start_date || null,
      })),
    });
  }, [data, reset]);


  const { data: partnersData } = usePartners();
  const { mutateAsync: updateProduct } = useUpdateCustomerProduct();
  const { t } = useTranslation();

  const hasPermissionToEditProduct = usePermissions([
    "manage_customers_products_edit",
  ]);
  const hasPermissionToCreateProduct = usePermissions([
    "manage_customers_products_create",
  ]);

  const hasPermissionToViewProductAllPrice = usePermissions([
    "customers_products_prices_view",
  ]);



  const onSubmit = (formData: any) => {
    const updatedProducts = data?.data?.reduce(
      (acc: any, product: any, index: number) => {
        const productData = formData.products[index];

        if (productData) {
          acc.push({
            product_id: product.id,
            creation_date: productData.creation_date || "",
            company_name: productData.company_name || "",
            product_name: productData.product_name || "",
            name: productData.name || "",
            status: productData.status || "",
            last_update: productData.last_update || "",
            commission: productData.commission || "",
            commission_date: productData.commission_date || "",
            premium_per_month: productData.premium_per_month || "",
            contract_start_date: productData.contract_start_date || "",
            contract_end_date: productData.contract_end_date || "",
          });
        }
        return acc;
      },
      []
    );

    updateProduct({
      costumer_id: id,
      products: updatedProducts,
      from_user_id: from_user_id
    }).then(() => {
      refetchProducts();
    })

  };

  const formatDate = (dateString: string) => {
    if (!dateString) {
      return t("Not Available");
    }
    const datePart = dateString.split("T")[0];
    const [year, month, day] = datePart.split("-");
    return `${day}/${month}/${year}`;
  };

  const getExpetedProvisionColor = useCallback(
    (value: number) => {
      if (value === 0) {
        return ''
      }

      if (value > 0) {
        return 'text-[#198754] !border-[#198754]'
      }

      return 'text-[#D61616] !border-[#D61616]'
    },
    [],
  )

  const [DeleteData, setDeleteData] = useState<any>({ id: '', name: '' })


  const hasPermissiontoDeleteProduct = usePermissions([
    "manage_customers_products_delete",
  ]);


  if (isLoading) {
    return <LoadingScreen />;
  }


  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <NoDataBoundary
        condition={data?.data?.length >= 1}
        fallback={<EmptyEntity name="Products" />}
      >
        <InputsFolder
          title={t("Products")}
          childrenClassName="flex flex-col gap-5"
        >
          <>
            {data?.data?.map?.((product: any, index: number) => {
              return (
                <Accordion
                  open={DeleteData.id === product.id ? true : undefined}
                  key={index}
                  classes={{ wrapper: "bg-white", title: "!w-full" }}
                >
                  <AccordionTitle subTitleClass="w-full">
                    <div className="flex items-center justify-between w-full">
                      <div className="flex items-center gap-[10px]">
                        <Icon iconType={IconType.KVG_ICON} className="flex-none" />
                        <span className="text-[#282D46] font-inter-medium capitalize max-w-[400px]">
                          {product.name} {product?.product_name} (
                          {product.company_name})
                        </span>
                      </div>
                      <div className="flex items-center gap-3 mr-[10px]">
                        <p className="text-[#979797] text-[14px] font-inter-regular">
                          {t("Created")}: {formatDate(product.creation_date)}
                        </p>{" "}
                        <p
                          className={`font-inter-medium text-[12px] min-w-[101px] capitalize p-2 border-[1px] rounded-[8px] ${product?.status === "Open (Consultant)"
                            ? "border-[#FFC107] text-[#FFC107]"
                            : product?.status === "Open (Office)"
                              ? "border-[#FFC107] text-[#FFC107]"
                              : product?.status === "Submitted"
                                ? "border-[#9F9F9F] text-[#9F9F9F]"
                                : product?.status === "Accepted"
                                  ? "border-[#64C764] text-[#64C764]"
                                  : product?.status === "Exclusion"
                                    ? "border-[#c74242] text-[#c74242]"
                                    : product?.status === "Provisioned"
                                      ? "border-[#198754] text-[#198754]"
                                      : product?.status === "Rejected"
                                        ? "border-[#DC3545] text-[#DC3545]"
                                        : product?.status === "Retreat"
                                          ? "border-[#9E0707] text-[#9E0707]"
                                          : product?.status === "Canceled"
                                            ? "border-[#E91D1D] text-[#E91D1D]"
                                            : product?.status === "Quality"
                                              ? "border-[#50B8E7] text-[#50B8E7]"
                                              : ""
                            }`}
                        >
                          {t(product?.status) ?? t("Pending")}
                        </p>
                        {hasPermissiontoDeleteProduct && product?.status !== 'Provisioned' && product?.status !== 'Canceled' ? (

                          <div className="flex-none cursor-pointer"
                            onClick={() => {
                              setDeleteData({ id: {costumer_id: id, product_id: product.id}, name: `${product.name} ${product?.product_name || ''} (${product.company_name})` })
                            }}>
                            <Icon iconType={IconType.DELETE_ICON} />
                          </div>
                        ): null}
                      </div>
                    </div>
                  </AccordionTitle>

                  <AccordionBody>
                    <div className="grid grid-cols-2 w-full gap-5 pb-5">
                      <input
                        type="hidden"
                        {...register(`products.${index}.id`)}
                        defaultValue={product.id}
                      />
                      <Controller
                        control={control}
                        name={`products.${index}.creation_date`}
                        render={({ field: { onChange, value } }) => (
                          <PrimaryDatePicker
                            label={t("Completion Date")}
                            startDate={value}
                            {...register(`products.${index}.creation_date`)}
                            setStartDate={(date: string) => onChange(date)}
                            defaultValue={product.creation_date}
                          />
                        )}
                      />
                      <div className="relative">
                        <Label text={t("Company")} />
                        <Controller
                          name={`products.${index}.company_name`}
                          control={control}
                          render={({ field }) => (
                            <Creatable<any, false>
                              {...field}
                              placeholder={t("Choose company")}
                              options={partnersData?.partners?.map?.(
                                (partner: any) => ({
                                  value: partner.name,
                                  label: partner.name,
                                })
                              )}
                              value={
                                field?.value
                                  ? {
                                    value: field?.value,
                                    label: field.value,
                                  }
                                  : null
                              }
                              className="cursor-pointer font-inter-regular react-select-container"
                              classNamePrefix="react-select"
                              onChange={(newValue: any, actionMeta: any) => {
                                if (
                                  actionMeta.action === "select-option" &&
                                  newValue?.value
                                ) {
                                  field.onChange(newValue.value);
                                }
                              }}
                              styles={customStyles}
                              isValidNewOption={() => false}
                              menuPosition={"fixed"}
                              menuPortalTarget={document.body}
                            />
                          )}
                        />
                      </div>
                      <Input
                        placeholder={t("Product Name")}
                        {...register(`products.${index}.product_name`)}
                        // error={errors.product_nam as any}
                        name={`products.${index}.product_name`}
                        label={t("Product Name")}
                      />

                      <div className="relative">
                        <Label text={t("Status")} />
                        <Controller
                          name={`products.${index}.status`}
                          control={control}
                          render={({ field }) => (
                            <Creatable<any, false>
                              {...field}
                              placeholder={t("Choose status")}
                              options={
                                product?.status === "Open (Consultant)"
                                  ? [
                                    {
                                      value: "Open (Consultant)",
                                      label: t("Open (Consultant)"),
                                    },
                                    {
                                      value: "Open (Office)",
                                      label: t("Open (Office)"),
                                    },
                                    {
                                      value: "Quality",
                                      label: t("Quality"),
                                    },
                                    {
                                      value: "Retreat",
                                      label: t("Retreat")
                                    },
                                  ]
                                  : product?.status === "Open (Office)"
                                    ? [
                                      {
                                        value: "Open (Office)",
                                        label: t("Open (Office)"),
                                      },
                                      {
                                        value: "Open (Consultant)",
                                        label: t("Open (Consultant)"),
                                      },
                                      {
                                        value: "Quality",
                                        label: t("Quality"),
                                      },
                                      {
                                        value: "Retreat",
                                        label: t("Retreat")
                                      },
                                    ]
                                    : product?.status === "Submitted"
                                      ? [
                                        {
                                          value: "Submitted",
                                          label: t("Submitted"),
                                        },
                                        {
                                          value: "Exclusion",
                                          label: t("Exclusion"),
                                        },
                                        {
                                          value: "Accepted",
                                          label: t("Accepted"),
                                        },
                                        {
                                          value: "Rejected",
                                          label: t("Rejected"),
                                        },
                                        {
                                          value: "Retreat",
                                          label: t("Retreat")
                                        },
                                      ] : product?.status === "Exclusion" ?
                                        [

                                          {
                                            value: "Accepted",
                                            label: t("Accepted"),
                                          },
                                          {
                                            value: "Rejected",
                                            label: t("Rejected"),
                                          },
                                          {
                                            value: "Retreat",
                                            label: t("Retreat")
                                          },
                                        ]
                                        : product?.status === "Accepted"
                                          ? [
                                            {
                                              value: "Accepted",
                                              label: t("Accepted"),
                                            },
                                            {
                                              value: "Provisioned",
                                              label: t("Provisioned"),
                                            },
                                            { value: "Retreat", label: t("Retreat") },
                                          ]
                                          : product?.status === "Rejected"
                                            ? [
                                              {
                                                value: "Rejected",
                                                label: t("Rejected"),
                                              },
                                            ]
                                            : product?.status === "Retreat"
                                              ? [{ value: "Retreat", label: t("Retreat") }]
                                              : product?.status === "Provisioned"
                                                ? [
                                                  {
                                                    value: "Provisioned",
                                                    label: t("Provisioned"),
                                                  },
                                                  {
                                                    value: "Canceled",
                                                    label: t("Canceled"),
                                                  },
                                                ]
                                                : product?.status === "Canceled"
                                                  ? [
                                                    {
                                                      value: "Canceled",
                                                      label: t("Canceled"),
                                                    },
                                                  ]
                                                  : []
                              }
                              value={
                                field?.value
                                  ? {
                                    value: field?.value,
                                    label: t(field.value) + (product?.has_exclusion === 1 ? ` (${t('Exclusion')})` : ''),
                                  }
                                  : null
                              }
                              className="cursor-pointer font-inter-regular react-select-container"
                              classNamePrefix="react-select"
                              onChange={(newValue: any, actionMeta: any) => {
                                if (
                                  actionMeta.action === "select-option" &&
                                  newValue?.value
                                ) {
                                  field.onChange(newValue.value);
                                }
                              }}
                              styles={customStyles}
                              isValidNewOption={() => false}
                              menuPosition={"fixed"}
                              menuPortalTarget={document.body}
                            />
                          )}
                        />
                      </div>
                      <Controller
                        control={control}
                        name={`products.${index}.contract_start_date`}
                        render={({ field: { onChange, value } }) => (
                          <PrimaryDatePicker
                            label={t("Contract Start Date")}
                            startDate={value}
                            setStartDate={(date: string) => onChange(date)}
                            defaultValue={product.creation_date}
                          />
                        )}
                      />


                      <Controller
                        control={control}
                        name={`products.${index}.contract_end_date`}
                        render={({ field: { onChange, value } }) => (
                          <PrimaryDatePicker
                            label={t("Contract End Date")}
                            startDate={value}
                            setStartDate={(date: string) => onChange(date)}
                            defaultValue={product.creation_date}
                          />
                        )}
                      />
                      {hasPermissionToViewProductAllPrice && (
                        <>
                          <Controller
                            control={control}
                            name={`products.${index}.premium_per_month`}
                            render={({ field }) => (
                              <CurrencyInput
                                placeholder={t("Premium per month")}
                                prefix="CHF "
                                value={field.value || ""}
                                onValueChange={(value) => {
                                  field.onChange(value || "");

                                }}
                                label="Premium per month"
                                disabled={product?.status === "Canceled"}
                                error={errors?.products && (errors.products as unknown as FieldErrorsImpl<any>[])[index]?.premium_per_month}
                              />
                            )}
                          />
                          {product?.commission_expected !== null && (
                            <Controller
                              control={control}
                              name={`products.${index}.commission_expected`}
                              render={({ field }) => (
                                <CurrencyInput
                                  placeholder={t("Provision expected")}
                                  prefix="CHF "
                                  value={field.value || ""}
                                  onValueChange={(value) => {
                                    field.onChange(value || "");
                                  }}
                                  label="Provision expected"
                                  error={errors?.products && (errors.products as unknown as FieldErrorsImpl<any>[])[index]?.commission_expected}
                                  disabled
                                />
                              )}
                            />

                          )}
                          {product?.commission_expected_difference !== null && (
                            <Controller
                              control={control}
                              name={`products.${index}.commission_expected_difference`}
                              render={({ field }) => {
                                
                                return (
                                <CurrencyInput
                                  prefix="CHF "
                                  value={product?.commission_expected_difference}
                                  onValueChange={(value) => {
                                    field.onChange(value || "");
                                  }}
                                  label="Provision expected difference"
                                  error={errors?.products && (errors.products as unknown as FieldErrorsImpl<any>[])[index]?.commission_expected_difference}
                                  disabled
                                  className={getExpetedProvisionColor(product.commission_expected_difference)}
                                />
                              )
                              }}
                            />
                          )}
                        </>
                      )}

                      <ConditionalRender
                        condition={watch(`products.${index}.status`) === 'Provisioned' || product?.status === "Provisioned" || product?.status === "Canceled"}
                      >
                        {hasPermissionToViewProductAllPrice ? (

                          <Controller
                            control={control}
                            name={`products.${index}.commission`}
                            render={({ field }) => (
                              <CurrencyInput
                                placeholder={t("Total Provision")}
                                prefix="CHF "
                                value={field.value || ""}
                                onValueChange={(value) => {
                                  field.onChange(value || "");

                                }}
                                label="Total Provision"
                                error={errors?.products && (errors.products as unknown as FieldErrorsImpl<any>[])[index]?.commission}
                                disabled={product?.status === "Canceled"}
                              />
                            )}
                          />
                        ) : (
                          <Input
                            placeholder={t("Provision")}
                            error={
                              errors.products &&
                              (
                                errors.products as unknown as FieldErrorsImpl<any>[]
                              )[index]?.commission
                            }
                            name={`products.${index}.commission`}
                            label={t("Provision")}
                            type="text"
                            classes={{ icon: "right-[40px] bg-white" }}
                            step="0.01"
                            disabled
                            value={
                              `CHF ${(product.prod_commission !== null) ? product?.prod_commission?.worker_commission : product.commission}`
                            }
                          />
                        )}


                        <Controller
                          control={control}
                          name={`products.${index}.commission_date`}
                          render={({ field: { onChange, value } }) => (
                            <PrimaryDatePicker
                              label={t("Provision Date")}
                              startDate={value}
                              isDisabled={product?.status === "Canceled"}
                              {...register(`products.${index}.commission_date`)}
                              setStartDate={(date: string) => onChange(date)}
                            />
                          )}
                        />
                      </ConditionalRender>
                    </div>
                  </AccordionBody>
                </Accordion>
              );
            })}
          </>
        </InputsFolder>
      </NoDataBoundary>
      <div className="flex justify-end gap-[26px] mt-5">
        <ConditionalRender condition={hasPermissionToCreateProduct}>
          <Button
            onClick={() => setFormStep && setFormStep(2)}
            type="button"
            className={`bg-transparent flex items-center gap-2 font-inter-medium text-[14px] text-[--theme]`}
          >
            <Icon iconType={IconType.PLUS_ICON} color={themeColors.primary} />
            {t("Add Product")}
          </Button>
        </ConditionalRender>
        <ConditionalRender condition={hasPermissionToEditProduct}>
          <IconButton type="submit">{t("Save Changes")}</IconButton>
        </ConditionalRender>
      </div>
      <DeleteProductModal
        setDeleteData={setDeleteData}
        DeleteData={DeleteData}
        hocisOpen={DeleteData.id !== ''}
        hocCurrentStep={1}
        hocToggleModal={() => { setDeleteData({ id: '', name: '' }) }}
        hocClasses={{
          modalClassName: "w-full sm:max-w-[600px]"
        }}
      />
    </Form>
  );
};

export default UpdateChooseProducts;
